import { getUserId } from './firebaseInit';
import { getRetrospectiveDocumentById } from './retrospectives';
import { getDefaultConverter } from '../utils/helpers/firestore';
import { Card } from '../@types/domain';
import {
  DocumentReference,
  FirestoreError,
  QuerySnapshot,
  Unsubscribe,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';

/**
 * Get retrospective cards collection
 * @param {string} retrospectiveId - The retrospective ID
 */

export function getRetrospectiveCardsCollection(retrospectiveId: string) {
  return collection(getRetrospectiveDocumentById(retrospectiveId), 'cards');
}

export function getRetrospectiveCardDocumentById(
  retrospectiveId: string,
  cardId: string
): DocumentReference<Card> {
  return doc(
    getRetrospectiveCardsCollection(retrospectiveId),
    cardId
  ).withConverter(getDefaultConverter<Card>());
}

const createCard = async (retrospectiveId, description, columnName, rank) => {
  const documentReference = await addDoc(
    getRetrospectiveCardsCollection(retrospectiveId),
    {
      description,
      columnName,
      rank,
      votes: [],
      actionItems: [],
      createdOn: serverTimestamp(),
      createdBy: getUserId(),
    }
  );
  return documentReference.id;
};

const getCards = async (retrospectiveId) => {
  const q = query(
    getRetrospectiveCardsCollection(retrospectiveId)
  ).withConverter(getDefaultConverter<Card>());
  const querySnapshot = await getDocs(q);
  const results: Card[] = [];
  for (const document_ of querySnapshot.docs) {
    results.push(document_.data());
  }
  return results;
};

const updateCard = async (retrospectiveId, id, fields) => {
  await updateDoc(
    getRetrospectiveCardDocumentById(retrospectiveId, id),
    fields
  );
};

const deleteCard = async (retrospectiveId, id) => {
  await deleteDoc(getRetrospectiveCardDocumentById(retrospectiveId, id));
};

const combineCards = async (
  retrospectiveId,
  sourceCardId,
  targetCardId,
  newDescription
) => {
  const sourceCardReference = getRetrospectiveCardDocumentById(
    retrospectiveId,
    sourceCardId
  );
  const targetCardReference = getRetrospectiveCardDocumentById(
    retrospectiveId,
    targetCardId
  );

  const batch = writeBatch(getFirestore());
  batch.update(targetCardReference, { description: newDescription });
  batch.delete(sourceCardReference);
  return batch.commit();
};

const listenCardsChange = (
  retrospectiveId: string,
  callback: (snapshot: QuerySnapshot) => void,
  errorCallback?: (error: FirestoreError) => void
): Unsubscribe => {
  return onSnapshot(
    getRetrospectiveCardsCollection(retrospectiveId),
    callback,
    errorCallback
  );
};

export {
  createCard,
  getCards,
  updateCard,
  deleteCard,
  listenCardsChange,
  combineCards,
};
